import styled, { keyframes } from 'styled-components';
import { key } from 'styled-theme';

// Animations
const fadeIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

const slideUp = keyframes`
  from { 
    transform: translateY(100%); 
    opacity: 0; 
  }
  to { 
    transform: translateY(0); 
    opacity: 1; 
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const wave = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-10deg); }
  75% { transform: rotate(10deg); }
`;

const listening = keyframes`
  0%, 100% { 
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); 
  }
  50% { 
    box-shadow: 0 0 0 15px rgba(59, 130, 246, 0); 
  }
`;

const typing = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-10px); }
`;

const faqItemIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Main wrapper
export const ChatBotWrapper = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

// Chat button with modern design
export const ChatButton = styled.button`
  width: 62px;
  height: 62px;
  border-radius: 50%;
  border: none;
  background: #fff;
  color: white;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 4px 4px 50px rgb(72, 176, 238);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: 2px solid #3b82f6;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
    animation: ${wave} 0.5s ease-in-out;

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
    box-shadow:
      0 8px 25px rgba(102, 126, 234, 0.3),
      0 0 0 3px rgba(102, 126, 234, 0.2);
  }
`;

// Chat popup with glassmorphism effect
export const ChatPopup = styled.div`
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 380px;
  height: 500px;
  background: rgba(255, 255, 255, 1);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  transform: ${(props) =>
    props.isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)'};
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${(props) => (props.isOpen ? fadeIn : 'none')} 0.4s ease-out;

  @media (max-width: 480px) {
    width: calc(100vw - 48px);
    height: 450px;
    bottom: 80px;
    right: -12px;
  }
`;

// Messages container
export const ChatMessages = styled.div`
  flex: 1;
  padding: 24px 20px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 2px;

    &:hover {
      background: rgba(102, 126, 234, 0.5);
    }
  }

  .loaderSecondary {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 16px 20px;

    &::before,
    &::after,
    & {
      content: '';
      width: 8px;
      height: 8px;
      background: #667eea;
      border-radius: 50%;
      animation: ${typing} 1.4s infinite ease-in-out;
    }

    &::before {
      animation-delay: -0.32s;
    }

    & {
      animation-delay: -0.16s;
    }
  }
`;

// Individual message styling
export const Message = styled.div`
  max-width: 80%;
  padding: 14px 18px;
  border-radius: ${(props) => (props.user ? '20px 20px 6px 20px' : '20px 20px 20px 6px')};
  align-self: ${(props) => (props.user ? 'flex-end' : 'flex-start')};
  background: ${(props) =>
    props.user ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(247, 250, 252, 0.9)'};
  color: ${(props) => (props.user ? 'white' : '#374151')};
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  position: relative;
  animation: ${slideUp} 0.3s ease-out;
  box-shadow: ${(props) =>
    props.user ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'};
  border: ${(props) => (props.user ? 'none' : '1px solid rgba(229, 231, 235, 0.8)')};

  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    ${(props) =>
      props.user
        ? `
      bottom: 0;
      right: -5px;
      border-left: 10px solid #764ba2;
      border-top: 20px solid transparent;
    `
        : `
      bottom: 0;
      left: -8px;
      border-right: 8px solid rgba(247, 250, 252, 0.8);
      border-top: 8px solid transparent;
    `}
  }
`;

// Input wrapper
export const UserInputWrapper = styled.div`
  padding: 20px;
  border-top: 1px solid rgba(229, 231, 235, 0.5);
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 0 0 24px 24px;
  position: relative;
`;

// Text input
export const UserInput = styled.input`
  flex: 1;
  padding: 14px 16px;
  border: 2px solid rgba(229, 231, 235, 0.5);
  border-radius: 16px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  outline: none;
  border-color: #667eea;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    background: white;
  }

  &:read-only {
    background: rgba(249, 250, 251, 0.9);
    cursor: not-allowed;
  }
`;

// Voice button
export const VoiceButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: rgba(229, 231, 235, 0.5);
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    transform: scale(1.05);
  }

  &.listening {
    background: #667eea;
    color: white;
    animation: ${listening} 2s infinite;

    &:hover {
      background: #5a67d8;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }
`;

// Send button
export const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    background: rgba(156, 163, 175, 0.5);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }
`;

// Listening popup
export const ListeningPopup = styled.div`
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(102, 126, 234, 0.95);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  animation: ${pulse} 2s infinite;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(102, 126, 234, 0.95);
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: #f5f5f5;
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  color: #666;

  &:hover {
    background: #e0e0e0;
    transform: scale(1.05);
    color: red;
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }
`;

// ─── CHANGED: FAQContainer, FAQList, FAQButton ────────────────────────────────

export const FAQContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  &::before {
    content: 'How can I help you?';
    display: block;
    font-size: 16px;
    font-weight: 700;
    color: #1a1a2e;
    text-align: center;
    padding: 0 0 14px;
    letter-spacing: -0.2px;
    font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  }
`;

export const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 0 16px 8px;
  box-sizing: border-box;
`;

export const FAQButton = styled.button`
  width: 100%;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 12px;
  padding: 13px 16px;
  font-size: 13.5px;
  font-weight: 500;
  color: #1a1a2e;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  box-sizing: border-box;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  animation: ${faqItemIn} 0.3s ease both;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};

  &:nth-child(1) {
    animation-delay: 0.04s;
  }
  &:nth-child(2) {
    animation-delay: 0.08s;
  }
  &:nth-child(3) {
    animation-delay: 0.12s;
  }
  &:nth-child(4) {
    animation-delay: 0.16s;
  }
  &:nth-child(5) {
    animation-delay: 0.2s;
  }
  &:nth-child(6) {
    animation-delay: 0.24s;
  }

  &::after {
    content: '›';
    font-size: 20px;
    font-weight: 300;
    color: #667eea;
    flex-shrink: 0;
    line-height: 1;
    transition: transform 0.2s ease;
  }

  &:hover {
    border-color: #0056b3;
    color: #0056b3;

    &::after {
      transform: translateX(3px);
    }
  }

  &:active {
    transform: scale(0.99);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 11px 14px;
  }
`;
